import dayjs from 'dayjs';

/**
 * Convert serialised dateRange strings back to dayjs objects in a form data record.
 * Mutates `data` in-place and returns it for convenience.
 */
export function restoreDateRanges(data: Record<string, any>, fields: any[]): Record<string, any> {
    fields.forEach((field: any) => {
        if (field.type === 'dateRange' && Array.isArray(data[field.name])) {
            data[field.name] = data[field.name].map((d: any) => (d ? dayjs(d) : null));
        }
    });
    return data;
}

/** Validate a date string — returns empty string for "Invalid Date" sentinel. */
export function checkDate(date: any): string {
    if (date === 'Invalid Date') return '';
    return date;
}

/** Build form data from a saved filter (e.g. quick-filter chip click). */
export function mapFilterToFormData(filter: any, fields: any[], filterNameKey: string, listValues?: any): Record<string, any> {
    const initialData: Record<string, any> = {};

    fields.forEach((field: any) => {
        initialData[field.name] = '';

        if (field.type === 'select') {
            const reqVal = field?.reqKey ? filter?.[field.reqKey] : undefined;
            const nameVal = filter?.[field?.name];
            // Also check altKey for backward compatibility (e.g., crop_code might be stored as crop)
            const altVal = field?.altKey ? filter?.[field.altKey] : undefined;
            const rawVal = reqVal ?? nameVal ?? altVal;
            let values = Array.isArray(rawVal)
                ? rawVal
                : (typeof rawVal === 'string'
                    ? rawVal.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0)
                    : (rawVal !== undefined && rawVal !== null && String(rawVal).trim().length > 0 ? [rawVal] : []));
            
            // Reverse transform: if field has transformKey, convert saved transformKey values back to listKey values
            if (field?.transformKey && field?.list && field?.listKey && listValues && values.length > 0) {
                const listData = Array.isArray(field.list) ? field.list : (listValues?.[field.list] ?? []);
                values = values.map((val: any) => {
                    const item = listData.find((item: any) => item[field.transformKey] === val);
                    return item?.[field.listKey] ?? val;
                });
            }
            
            initialData[field.name] = values;
        } else if (field.type === 'dateRange') {
            const fieldSplit = field.reqKey?.split('-') ?? [];
            const fromDate = fieldSplit[0] ? filter[fieldSplit[0]] : undefined;
            const toDate = fieldSplit[1] ? filter[fieldSplit[1]] : undefined;
            initialData[field.name] = fromDate && toDate ? [dayjs(fromDate), dayjs(toDate)] : [null, null];
        } else if (field.type === 'PeoplePicker') {
            const pickerValue = field?.reqKey ? filter?.[field.reqKey] : filter?.[field?.name];
            const names = typeof pickerValue === 'string'
                ? pickerValue.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0)
                : [];
            initialData[field.name] = names.map((name: string) => ({ displayName: name }));
        } else if (field.type === 'radio') {
            // Radio fields: preserve boolean values (true/false) and null
            const reqVal = field?.reqKey ? filter?.[field.reqKey] : undefined;
            const nameVal = filter?.[field?.name];
            const value = reqVal !== undefined ? reqVal : nameVal;
            // Only set the value if it's explicitly defined in the filter (including false)
            initialData[field.name] = value !== undefined ? value : field?.defaultValue ?? null;
        } else {
            const reqVal = field?.reqKey ? filter?.[field.reqKey] : undefined;
            const nameVal = filter?.[field?.name];
            initialData[field.name] = reqVal ?? nameVal;
        }
    });

    initialData['filter_name'] = filter[filterNameKey];
    return initialData;
}

export interface ChipDescriptor {
    name: string;
    type: string;
    lable: string;
    value: string;
}

/** Build filter chip descriptors from current form data. */
export function buildChipDescriptors(formData: Record<string, any>, fields: any[], listValues: any): ChipDescriptor[] {
    const chips: ChipDescriptor[] = [];

    const resolveFieldList = (field: any): any[] =>
        Array.isArray(field?.list) ? field.list : (listValues?.[field?.list] ?? []);

    for (let c = 0; c < fields?.length; c++) {
        const field = fields[c];
        const formVal = formData?.[field?.name];

        if (field?.type === 'select') {
            if (Array.isArray(formVal) && formVal.length > 0) {
                if (field?.displayNameOnSel) {
                    const options = resolveFieldList(field);
                    const getNameById = (id: any) =>
                        options?.find((m: any) => m[field?.['listKey']] === id)?.[field?.['listValue']] ?? id;
                    const names = formVal.map((id: any) => getNameById(id));
                    chips.push({ name: field.name, type: field.type, lable: field.label, value: names.join(',') });
                } else {
                    chips.push({ name: field.name, type: field.type, lable: field.label, value: formVal.join(',') });
                }
            }
        } else if (field?.type === 'radio') {
            if (formVal !== undefined && formVal !== null && formVal !== '') {
                const options = Array.isArray(field?.list)
                    ? field.list
                    : (listValues?.[field?.list] ?? []);
                const matchedOption = options.find((option: any) => option?.[field?.listKey] === formVal);
                const chipValue = field?.displayNameOnSel
                    ? (matchedOption?.[field?.listValue] ?? String(formVal))
                    : String(formVal);
                chips.push({ name: field.name, type: field.type, lable: field.label, value: chipValue });
            }
        } else if (field?.type === 'number' && formVal !== undefined && formVal !== null && formVal !== '' && formVal !== field?.defaultValue) {
            chips.push({ name: field.name, type: field.type, lable: field.label, value: String(formVal) });
        } else if ((field?.type === 'text' || field?.type === 'textMulti') && formVal && String(formVal).trim().length > 0) {
            chips.push({ name: field.name, type: field.type, lable: field.label, value: formVal });
        }
    }

    return chips;
}

/** Get the reset value for a field type (used when deleting a chip). */
export function getFieldResetValue(type: string, defaultValue?: any): any {
    if (type === 'select') return [];
    if (type === 'radio') return defaultValue ?? null;
    if (type === 'dateRange') return [null, null];
    if (type === 'number') return defaultValue ?? 0;
    return '';
}
